/**
 * The SVG active-content rules, with the DOM injected by the caller.
 *
 * SVG is the only image format we accept that can carry executable code, and Cloudinary's format
 * allow-list cannot catch it — a malicious SVG is a valid SVG. So the same checks run twice: the
 * server passes a jsdom window (`svg-safety.ts`), the browser passes its own.
 *
 * Keep this file free of `payload` and `jsdom` imports so it can be bundled for the admin panel.
 */
import type createDOMPurify from 'dompurify';

type Purifier = ReturnType<typeof createDOMPurify>;

/**
 * Re-admits inert markup real editors emit: icon sprites, Inkscape bookkeeping, accessibility hints.
 *
 * `href` is admitted by name only, and DOMPurify still rejects a `javascript:` value. Never add
 * `set` or `animate` here — both can retarget `href` at runtime.
 */
const SANITIZER_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
  ADD_TAGS: ['use', 'symbol', 'marker', 'pattern', 'metadata', 'sodipodi:namedview'],
  ADD_ATTR: [
    'href',
    'xlink:href',
    'role',
    'aria-label',
    'xml:lang',
    'xml:space',
    'from',
    'to',
    'values',
    'attributeName',
    'dur',
    'begin',
    'repeatCount',
    'type',
    'keyTimes',
    'calcMode',
    'restart',
    'additive',
    'accumulate',
  ],
};

const INERT_ATTRIBUTE = /^(xmlns:|xml:|sodipodi:|inkscape:|rdf:|cc:|dc:|dcterms:|serif:|i:|x:|graph:)/i;
/** `body` included because DOMPurify parses the string as HTML and then drops the wrapper it added. */
const INERT_ELEMENT = /^(body|#comment|metadata|sfw|slices|slicesourcebounds|rdf:|cc:|dc:|sodipodi:|inkscape:)/i;

/** DOMPurify does not parse CSS, so a stylesheet that fetches or executes survives it untouched. */
const ACTIVE_STYLE_RULE = /(javascript|vbscript):|data:\s*text\/html|(^|[^-\w])@import\b/i;

const STYLE_URL = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)]*))(?:\)|$)/gi;

/** Applies the `isLocalReference` rule to every `url()` a stylesheet names. */
const cssReferencesOutsideTheFile = (css: string): boolean =>
  [...css.matchAll(STYLE_URL)].some(([, doubleQuoted, singleQuoted, bare]) => {
    const target = (doubleQuoted ?? singleQuoted ?? bare ?? '').trim();

    // A CSS escape can spell anything — `\73 vg` is `svg`. Nothing legitimate needs one in a URL.
    return target.includes('\\') || !isLocalReference(target);
  });

/**
 * A `#fragment` points inside the file. A `data:` raster is the file. Anything else is fetched when
 * the SVG renders, from a server we do not control, so the bytes we scanned are not the bytes served.
 */
const isLocalReference = (target: string): boolean => target.startsWith('#') || /^data:image\/(?!svg)/i.test(target);

const REFERENCE_ATTRIBUTES = ['href', 'xlink:href'];

/**
 * Attributes whose value is CSS, or a CSS `url()`, so they can name a file to fetch on render.
 * DOMPurify keeps every one of them and reads none of them.
 */
const CSS_VALUED_ATTRIBUTES = ['style', 'fill', 'stroke', 'filter', 'mask', 'clip-path', 'marker-start', 'marker-mid', 'marker-end'];

/**
 * DOMPurify keeps `href` on `use` and `image`, and allows an http(s) value.
 *
 * Every attribute is checked, not the first one present: an element may carry both, and the stored
 * file is the original bytes, so a renderer that prefers the other one still reaches outside.
 */
const referencesOutsideTheFile = (root: Element): boolean =>
  [root, ...root.querySelectorAll('*')].some(
    (element) =>
      REFERENCE_ATTRIBUTES.some((name) => {
        const target = element.getAttribute(name);

        return target !== null && !isLocalReference(target.trim());
      }) ||
      // `fill="url(https://…)"` fetches on render exactly as the same rule inside `<style>` would.
      CSS_VALUED_ATTRIBUTES.some((name) => cssReferencesOutsideTheFile(element.getAttribute(name) ?? '')),
  );

/**
 * DOMPurify tracks the bypass vectors, so anything it strips beyond inert markup is active content.
 *
 * An attribute stripped from an inert element is inert too: Inkscape writes `pagecolor` and
 * `bordercolor` on `sodipodi:namedview`, which renders nothing.
 */
const carriesActiveContent = (purifier: Purifier, svg: string): boolean => {
  // `RETURN_DOM` hands back the wrapper element, which DOMPurify types only as `Node`.
  const sanitized = purifier.sanitize(svg, { ...SANITIZER_CONFIG, RETURN_DOM: true }) as Element;

  const strippedActiveMarkup = purifier.removed.some((removal) =>
    'element' in removal
      ? !INERT_ELEMENT.test(removal.element.nodeName?.toLowerCase() ?? '')
      : !INERT_ATTRIBUTE.test(removal.attribute?.name ?? '') && !INERT_ELEMENT.test(removal.from?.nodeName?.toLowerCase() ?? ''),
  );

  return (
    strippedActiveMarkup ||
    referencesOutsideTheFile(sanitized) ||
    [...sanitized.querySelectorAll('style')].some((rules) => {
      const css = rules.textContent ?? '';

      return ACTIVE_STYLE_RULE.test(css) || cssReferencesOutsideTheFile(css);
    })
  );
};

/** Entities never reach the DOM: they are resolved, or dropped, by the parser itself. */
const ENTITY_DECLARATION = /<!ENTITY/i;

const FETCHING_INSTRUCTION = /<\?(?!xml[\s?])/i;

/** SVG is the one format we accept that can carry executable content. */
export const svgActiveContentMessage = (filename: string): string =>
  `"${filename}" is an SVG carrying active content (script, event handler, entity or external reference).`;

export const createSvgActiveContentCheck =
  (purifier: Purifier) =>
  (svg: string): boolean =>
    ENTITY_DECLARATION.test(svg) || FETCHING_INSTRUCTION.test(svg) || carriesActiveContent(purifier, svg);
